/**
 * Enhanced AppContext
 *
 * Centralized state management for Taskometer 2.0
 * Uses structured reducers for different entity types with audit logging.
 *
 * Features:
 * - Separate state slices for tasks, slots, tags, types, schedules
 * - Automatic audit logging for all state changes
 * - Integration with DatabaseAdapter for persistence
 * - Backwards compatibility with legacy state structure
 */

import { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { addDays, format } from 'date-fns';
import { getAdapter } from '../services/database';
import { createTask, migrateLegacyTask, generateNextRecurrence, TASK_STATUSES } from '../models/Task';
import { createCalendarSlot, getSlotStartDateTime } from '../models/CalendarSlot';
import { createTag, DEFAULT_TAGS } from '../models/Tag';
import { createTaskType, DEFAULT_TASK_TYPES, LEGACY_TASK_TYPES } from '../models/TaskType';
import { createSchedule, createScheduleBlock, BLOCK_CATEGORIES } from '../models/Schedule';
import { createAuditEntry, AUDIT_ACTIONS, ENTITY_TYPES, CHANGE_SOURCES } from '../models/AuditEntry';
import { findOptimalTimeSlot, batchScheduleTasks } from '../utils/intelligentScheduler';
import { batchMatchTasksToSlots } from '../utils/slotMatcher';
import { getActiveSchedule as getLegacyActiveSchedule } from '../utils/scheduleTemplates';
import { scheduleTask as engineScheduleTask, scheduleMultipleTasks as engineScheduleMultiple, applyEventOverride } from '../utils/schedulingEngine';
import { log as telemetryLog, snapshotState } from '../services/telemetry';
import {
  DEFAULT_WHEELS as SEED_WHEELS,
  DEFAULT_TASK_TYPES as SEED_TASK_TYPES,
  DEFAULT_DAY_WHEEL_ID as SEED_DAY_WHEEL_ID,
} from '../defaults/defaultSchedule';

// ============================================
// CONTEXT CREATION
// ============================================

export const AppContext = createContext();

// ============================================
// INITIAL STATE
// ============================================

const getInitialDate = () => {
  const nd = new Date();
  return {
    day: format(nd, 'dd'),
    dayDisplay: format(nd, 'd'),
    month: format(nd, 'MM'),
    monthDisplay: format(nd, 'MMM'),
    year: format(nd, 'y'),
    weekday: format(nd, 'EEEE'),
  };
};

const initialState = {
  // Core entities
  tasks: [],
  slots: [],
  tags: [...DEFAULT_TAGS],
  taskTypes: [...DEFAULT_TASK_TYPES],
  schedules: [],
  palaces: [],

  // Active selections
  activeSchedule: null,
  activeScheduleId: null,

  // UI state
  date: getInitialDate(),

  // Audit log (in-memory, persisted separately)
  auditLog: [],

  // Loading/error states
  isLoading: true,
  error: null,
  isInitialized: false,

  // Legacy compatibility
  items: [], // Alias for tasks

  // Settings
  settings: {
    autoSchedule: true,
    showCompletedTasks: false,
    defaultView: 'week',
    theme: 'dark',
    defaultDaySlots: [],
    breakDuration: 0,
    silenceOverflowNotifications: false
  }
};

// ============================================
// ACTION TYPES
// ============================================

export const ACTION_TYPES = {
  // Initialization
  INIT_STATE: 'INIT_STATE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',

  // Task actions
  ADD_TASK: 'ADD_TASK',
  UPDATE_TASK: 'UPDATE_TASK',
  DELETE_TASK: 'DELETE_TASK',
  COMPLETE_TASK: 'COMPLETE_TASK',
  PAUSE_TASK: 'PAUSE_TASK',
  RESUME_TASK: 'RESUME_TASK',
  RESCHEDULE_TASK: 'RESCHEDULE_TASK',
  ADD_SUBTASK: 'ADD_SUBTASK',
  TOGGLE_SUBTASK: 'TOGGLE_SUBTASK',
  DELETE_SUBTASK: 'DELETE_SUBTASK',
  START_TIMER: 'START_TIMER',
  STOP_TIMER: 'STOP_TIMER',
  BATCH_UPDATE_TASKS: 'BATCH_UPDATE_TASKS',
  SCHEDULE_TASKS: 'SCHEDULE_TASKS',
  RESCHEDULE_ALL_TASKS: 'RESCHEDULE_ALL_TASKS',

  // Slot actions
  ADD_SLOT: 'ADD_SLOT',
  UPDATE_SLOT: 'UPDATE_SLOT',
  DELETE_SLOT: 'DELETE_SLOT',
  ASSIGN_TASK_TO_SLOT: 'ASSIGN_TASK_TO_SLOT',
  CLEAR_SLOT: 'CLEAR_SLOT',
  BATCH_CREATE_SLOTS: 'BATCH_CREATE_SLOTS',
  CLEAR_SLOTS_FOR_DATE: 'CLEAR_SLOTS_FOR_DATE',

  // Tag actions
  ADD_TAG: 'ADD_TAG',
  UPDATE_TAG: 'UPDATE_TAG',
  DELETE_TAG: 'DELETE_TAG',

  // Task Type actions
  ADD_TASK_TYPE: 'ADD_TASK_TYPE',
  UPDATE_TASK_TYPE: 'UPDATE_TASK_TYPE',
  DELETE_TASK_TYPE: 'DELETE_TASK_TYPE',

  // Schedule actions
  ADD_SCHEDULE: 'ADD_SCHEDULE',
  UPDATE_SCHEDULE: 'UPDATE_SCHEDULE',
  DELETE_SCHEDULE: 'DELETE_SCHEDULE',
  SET_ACTIVE_SCHEDULE: 'SET_ACTIVE_SCHEDULE',
  APPLY_SCHEDULE: 'APPLY_SCHEDULE',
  APPLY_SCHEDULE_BLOCKS: 'APPLY_SCHEDULE_BLOCKS',

  // Palace actions
  ADD_PALACE: 'ADD_PALACE',
  UPDATE_PALACE: 'UPDATE_PALACE',
  DELETE_PALACE: 'DELETE_PALACE',
  ADD_PALACE_LOCATION: 'ADD_PALACE_LOCATION',
  UPDATE_PALACE_LOCATION: 'UPDATE_PALACE_LOCATION',
  DELETE_PALACE_LOCATION: 'DELETE_PALACE_LOCATION',

  // Settings actions
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',

  // Date actions
  UPDATE_DATE: 'UPDATE_DATE',
  DAILY_RESET: 'DAILY_RESET',

  // Audit actions
  ADD_AUDIT_ENTRY: 'ADD_AUDIT_ENTRY',

  // Legacy compatibility
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  DELETE_ITEM: 'DELETE_ITEM',
  RESET_ALL: 'RESET_ALL',
  APPLY_TEMPLATE_BLOCKS: 'APPLY_TEMPLATE_BLOCKS',
  AUTO_SLOT_TASKS_INTO_TEMPLATE: 'AUTO_SLOT_TASKS_INTO_TEMPLATE'
};

function normalizeTemplateBlockToSlot(block = {}, index = 0) {
  const date = resolveTemplateBlockDate(block);
  const sourceScheduleId = block.sourceScheduleId || block.templateId || null;
  const sourceBlockId = block.sourceBlockId ||
    block.id ||
    `${sourceScheduleId || 'template'}-${date}-${index}`;

  return {
    date,
    startTime: resolveTemplateBlockTime(block.start, block.startTime, '09:00'),
    endTime: resolveTemplateBlockTime(block.end, block.endTime, '10:00'),
    // Keep template category/type for slot labeling and matching.
    slotType: block.slotType || block.type || block.category || null,
    allowedTags: Array.isArray(block.allowedTags) ? block.allowedTags : [],
    label: block.label || block.name || `${block.type || block.category || 'Template'} block`,
    description: block.description || null,
    color: block.color,
    flexibility: block.flexibility || 'preferred',
    sourceScheduleId,
    sourceBlockId,
    isRecurring: false
  };
}

function resolveTemplateBlockDate(block = {}) {
  if (typeof block.date === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(block.date)) {
      return block.date;
    }
    const parsedDate = new Date(block.date);
    if (!Number.isNaN(parsedDate.getTime())) {
      return format(parsedDate, 'yyyy-MM-dd');
    }
  }

  if (block.date instanceof Date && !Number.isNaN(block.date.getTime())) {
    return format(block.date, 'yyyy-MM-dd');
  }

  if (block.startTime) {
    const parsedStart = new Date(block.startTime);
    if (!Number.isNaN(parsedStart.getTime())) {
      return format(parsedStart, 'yyyy-MM-dd');
    }
  }

  return format(new Date(), 'yyyy-MM-dd');
}

function resolveTemplateBlockTime(rawTime, isoTime, fallback) {
  if (typeof rawTime === 'string') {
    const strictMatch = rawTime.match(/^(\d{2}):(\d{2})$/);
    if (strictMatch) {
      return rawTime;
    }
    const looseMatch = rawTime.match(/^(\d{1,2}):(\d{2})$/);
    if (looseMatch) {
      return `${looseMatch[1].padStart(2, '0')}:${looseMatch[2]}`;
    }
  }

  if (isoTime) {
    const parsedTime = new Date(isoTime);
    if (!Number.isNaN(parsedTime.getTime())) {
      return format(parsedTime, 'HH:mm');
    }
  }

  return fallback;
}

const DEFAULT_DAY_SLOT_SOURCE_ID = 'default-day-slots';

function getTaskIdentifier(task) {
  if (!task) return null;
  return task.id?.toString() || task.key?.toString() || null;
}

function getDefaultDaySlotsFromSettings(settings = {}) {
  if (!Array.isArray(settings.defaultDaySlots)) {
    return [];
  }

  return settings.defaultDaySlots.filter((slot) =>
    slot &&
    typeof slot.day === 'string' &&
    typeof slot.startTime === 'string' &&
    typeof slot.endTime === 'string'
  );
}

function buildDefaultSlotId(template, date) {
  const templateId = template.id ||
    `${template.day}-${template.startTime}-${template.endTime}-${template.slotType || 'any'}`;
  return `default-slot-${templateId}-${date}`;
}

function generateDefaultSlotsFromSettings(state, daysAhead = 21) {
  const templateSlots = getDefaultDaySlotsFromSettings(state.settings);
  if (templateSlots.length === 0) {
    return [];
  }

  const existingIds = new Set((state.slots || []).map((slot) => slot.id));
  const generated = [];

  for (let offset = 0; offset < daysAhead; offset += 1) {
    const targetDate = addDays(new Date(), offset);
    const dateString = format(targetDate, 'yyyy-MM-dd');
    const dayName = format(targetDate, 'EEEE').toLowerCase();

    templateSlots
      .filter((template) => template.day.toLowerCase() === dayName)
      .forEach((template, index) => {
        const slotId = buildDefaultSlotId(template, dateString);
        if (existingIds.has(slotId)) {
          return;
        }

        const generatedSlot = createCalendarSlot({
          id: slotId,
          date: dateString,
          startTime: template.startTime,
          endTime: template.endTime,
          slotType: template.slotType || null,
          allowedTags: Array.isArray(template.allowedTags) ? template.allowedTags : [],
          label: template.label || `${template.slotType || 'Task'} slot`,
          description: template.description || null,
          color: template.color,
          flexibility: template.flexibility || 'fixed',
          sourceScheduleId: DEFAULT_DAY_SLOT_SOURCE_ID,
          sourceBlockId: template.id || `${template.day}-${index}`,
          isRecurring: false
        });

        generated.push(generatedSlot);
        existingIds.add(slotId);
      });
  }

  return generated;
}

/**
 * Backfill: after slots are created or freed, try to place all unscheduled
 * pending tasks using the unified scheduling engine.  Returns patched
 * { tasks, slots } or null when nothing changed.
 */
function backfillUnscheduledTasks(state, timestamp) {
  const unscheduled = state.tasks.filter(
    t => t.status === 'pending' && !t.scheduledTime && !t.specificTime && !t.isTemplateBlock
  );
  if (unscheduled.length === 0) return null;

  const unscheduledIds = new Set(
    unscheduled.map(t => getTaskIdentifier(t)).filter(Boolean)
  );

  const stateForEngine = {
    slots: state.slots,
    tasks: state.tasks.filter(t => {
      const id = getTaskIdentifier(t);
      return id ? !unscheduledIds.has(id) : true;
    }),
    settings: state.settings,
    taskTypes: state.taskTypes,
  };

  const results = engineScheduleMultiple(unscheduled, stateForEngine);
  const scheduledMap = new Map();
  const slotUpdates = new Map();

  results.forEach(({ task, result }) => {
    if (!result) return;
    const taskId = getTaskIdentifier(task);
    if (!taskId) return;
    scheduledMap.set(taskId, {
      scheduledTime: result.scheduledTime,
      scheduledFor: result.scheduledTime,
      specificTime: result.specificTime,
      specificDay: result.specificDay,
      scheduledSlotId: result.slotId || null,
      metadata: {
        ...(task.metadata || {}),
        schedulingReason: result.reason,
        schedulingConfidence: result.confidence,
        scheduledLabel: result.label,
        scheduledDate: result.date,
        overflowed: result.overflowed || false,
        overflowDaysAhead: result.overflowDaysAhead || 0,
      },
    });
    if (result.slotId) {
      slotUpdates.set(result.slotId, taskId);
    }
  });

  if (scheduledMap.size === 0) return null;

  const newTasks = state.tasks.map(task => {
    const taskId = getTaskIdentifier(task);
    const updates = scheduledMap.get(taskId);
    if (!updates) return task;
    return { ...task, ...updates, updatedAt: timestamp };
  });

  const newSlots = state.slots.map(slot => {
    const assignedTaskId = slotUpdates.get(slot.id);
    if (!assignedTaskId) return slot;
    return { ...slot, assignedTaskId, updatedAt: timestamp };
  });

  return { tasks: newTasks, slots: newSlots };
}

function clearSlotAssignmentsForTaskIds(slots = [], taskIds = new Set(), timestamp) {
  if (taskIds.size === 0) {
    return slots;
  }

  return slots.map((slot) => {
    const assignedTaskId = slot.assignedTaskId?.toString();
    if (!assignedTaskId || !taskIds.has(assignedTaskId)) {
      return slot;
    }
    return {
      ...slot,
      assignedTaskId: null,
      updatedAt: timestamp
    };
  });
}

function scheduleTasksIntoSlots(state, tasksToSchedule = [], timestamp) {
  if (!Array.isArray(tasksToSchedule) || tasksToSchedule.length === 0) {
    return null;
  }

  const normalizedTasks = tasksToSchedule.map((task) => task || {}).map((task) => {
    return getTaskIdentifier(task) ? task : createTask(task);
  });

  const schedulingIds = new Set(
    normalizedTasks
      .map((task) => getTaskIdentifier(task))
      .filter(Boolean)
  );

  const generatedSlots = generateDefaultSlotsFromSettings(state, 21);
  const slotPool = [...state.slots, ...generatedSlots];
  if (slotPool.length === 0) {
    return null;
  }

  const existingTasksForConflicts = state.tasks.filter((task) => {
    const taskId = getTaskIdentifier(task);
    return taskId ? !schedulingIds.has(taskId) : true;
  });

  const matches = batchMatchTasksToSlots(
    normalizedTasks,
    slotPool,
    existingTasksForConflicts
  );

  const scheduledTaskMap = new Map();
  const slotAssignmentMap = new Map();
  const unscheduledTasks = [];

  matches.forEach((result) => {
    if (result.unscheduled || !result.slot) {
      unscheduledTasks.push(result.task);
      return;
    }

    const taskId = getTaskIdentifier(result.task);
    if (!taskId) {
      unscheduledTasks.push(result.task);
      return;
    }

    const slotStart = getSlotStartDateTime(result.slot);
    scheduledTaskMap.set(taskId, {
      ...result.task,
      scheduledTime: slotStart.toISOString(),
      scheduledFor: slotStart.toISOString(),
      specificTime: result.slot.startTime,
      specificDay: format(new Date(`${result.slot.date}T00:00:00`), 'EEEE'),
      scheduledSlotId: result.slot.id,
      failureReason: null
    });

    slotAssignmentMap.set(result.slot.id, taskId);
  });

  if (scheduledTaskMap.size === 0) {
    return null;
  }

  const updatedTasks = state.tasks.map((task) => {
    const taskId = getTaskIdentifier(task);
    if (!taskId) return task;
    const scheduledTask = scheduledTaskMap.get(taskId);
    if (!scheduledTask) return task;
    return {
      ...task,
      ...scheduledTask,
      updatedAt: timestamp
    };
  });

  const updatedTaskIds = new Set(updatedTasks.map((task) => getTaskIdentifier(task)).filter(Boolean));
  scheduledTaskMap.forEach((task, taskId) => {
    if (updatedTaskIds.has(taskId)) {
      return;
    }
    updatedTasks.push({
      ...task,
      updatedAt: timestamp
    });
  });

  const slotsById = new Map(state.slots.map((slot) => [slot.id, slot]));
  schedulingIds.forEach((taskId) => {
    const previousTask = state.tasks.find((task) => getTaskIdentifier(task) === taskId);
    const previousSlotId = previousTask?.scheduledSlotId;
    if (!previousSlotId || !slotsById.has(previousSlotId)) {
      return;
    }
    if (slotAssignmentMap.has(previousSlotId)) {
      return;
    }

    const previousSlot = slotsById.get(previousSlotId);
    slotsById.set(previousSlotId, {
      ...previousSlot,
      assignedTaskId: null,
      updatedAt: timestamp
    });
  });

  slotAssignmentMap.forEach((taskId, slotId) => {
    if (slotsById.has(slotId)) {
      const existingSlot = slotsById.get(slotId);
      slotsById.set(slotId, {
        ...existingSlot,
        assignedTaskId: taskId,
        updatedAt: timestamp
      });
      return;
    }

    const generatedSlot = generatedSlots.find((slot) => slot.id === slotId);
    if (generatedSlot) {
      slotsById.set(slotId, {
        ...generatedSlot,
        assignedTaskId: taskId,
        updatedAt: timestamp
      });
    }
  });

  return {
    tasks: updatedTasks,
    slots: Array.from(slotsById.values()),
    unscheduledTasks
  };
}

// ============================================
// REDUCER
// ============================================

function appReducer(state, action) {
  const now = new Date();
  const timestamp = now.toISOString();

  switch (action.type) {
    // ============================================
    // INITIALIZATION
    // ============================================

    case ACTION_TYPES.INIT_STATE: {
      return {
        ...state,
        ...action.payload,
        items: action.payload.tasks || action.payload.items || [],
        isLoading: false,
        isInitialized: true,
        error: null
      };
    }

    case ACTION_TYPES.SET_LOADING: {
      return { ...state, isLoading: action.payload };
    }

    case ACTION_TYPES.SET_ERROR: {
      return { ...state, error: action.payload, isLoading: false };
    }

    // ============================================
    // TASK ACTIONS
    // ============================================

    case ACTION_TYPES.ADD_TASK:
    case ACTION_TYPES.ADD_ITEM: {
      const taskData = action.payload || action.item;
      let newTask = createTask(taskData);
      const skipAutoSchedule = taskData?.autoSchedule === false;
      let newTasks = [...state.tasks, newTask];
      let newSlots = state.slots;

      if (state.settings.autoSchedule && !skipAutoSchedule && !newTask.scheduledTime && !newTask.specificTime) {
        // Primary path: unified scheduling engine (handles all cases)
        const engineResult = engineScheduleTask(newTask, {
          slots: state.slots,
          tasks: state.tasks,
          settings: state.settings,
          taskTypes: state.taskTypes,
        });

        if (engineResult) {
          const segments = Array.isArray(engineResult.segments) ? engineResult.segments : [];
          const segmentsTotal = segments.length + 1;
          const seriesId = segmentsTotal > 1
            ? (newTask.id || newTask.key || `series_${timestamp}_${Math.random().toString(36).slice(2, 7)}`)
            : null;
          const primaryDuration = typeof engineResult.primaryDuration === 'number'
            ? engineResult.primaryDuration
            : newTask.duration;

          newTask = {
            ...newTask,
            duration: primaryDuration,
            scheduledTime: engineResult.scheduledTime,
            scheduledFor: engineResult.scheduledTime,
            specificTime: engineResult.specificTime,
            specificDay: engineResult.specificDay,
            scheduledSlotId: engineResult.slotId || null,
            metadata: {
              ...newTask.metadata,
              schedulingReason: engineResult.reason,
              schedulingConfidence: engineResult.confidence,
              scheduledLabel: engineResult.label,
              scheduledDate: engineResult.date,
              overflowed: engineResult.overflowed || false,
              overflowDaysAhead: engineResult.overflowDaysAhead || 0,
              ...(seriesId && {
                seriesId,
                segmentIndex: 0,
                segmentsTotal,
                originalDuration: taskData?.duration ?? newTask.duration ?? primaryDuration,
                unplacedMinutes: engineResult.unplacedMinutes || 0,
              }),
            },
          };
          newTasks = [...state.tasks, newTask];

          // If the engine matched a slot, mark the slot's assigned task only
          // when the slot is still empty. Multiple tasks can share a slot;
          // each task carries its own scheduledSlotId, so we just keep the
          // slot's assignedTaskId as a first-occupant marker for code that
          // still reads it (e.g. event-override displacement).
          if (engineResult.slotId) {
            newSlots = state.slots.map(slot =>
              slot.id === engineResult.slotId && !slot.assignedTaskId
                ? { ...slot, assignedTaskId: newTask.id || newTask.key, updatedAt: timestamp }
                : slot
            );
          }

          // Fan out the continuation segments as linked child tasks so each
          // day the user sees the next chunk queued in its matching slot.
          if (segments.length > 0 && seriesId) {
            segments.forEach((seg, idx) => {
              const child = createTask({
                text: newTask.text,
                primaryType: newTask.primaryType,
                taskType: newTask.taskType,
                tags: newTask.tags,
                priority: newTask.priority,
                description: newTask.description,
                duration: seg.duration,
                scheduledTime: seg.scheduledTime,
                scheduledFor: seg.scheduledTime,
                specificTime: seg.specificTime,
                specificDay: seg.specificDay,
                scheduledSlotId: seg.slotId || null,
                metadata: {
                  schedulingReason: 'Continuation segment',
                  scheduledLabel: seg.label,
                  scheduledDate: seg.date,
                  seriesId,
                  segmentIndex: idx + 1,
                  segmentsTotal,
                },
              });
              newTasks = [...newTasks, child];
              if (seg.slotId) {
                newSlots = newSlots.map(slot =>
                  slot.id === seg.slotId && !slot.assignedTaskId
                    ? { ...slot, assignedTaskId: child.id, updatedAt: timestamp }
                    : slot
                );
              }
            });
          }
        } else {
          // Fallback: legacy slot-based scheduling
          const slotScheduled = scheduleTasksIntoSlots(
            { ...state, tasks: newTasks },
            [newTask],
            timestamp
          );

          if (slotScheduled) {
            newTasks = slotScheduled.tasks;
            newSlots = slotScheduled.slots;
          } else {
            const activeSchedule = state.activeSchedule || getLegacyActiveSchedule();
            if (activeSchedule) {
              const optimalSlot = findOptimalTimeSlot(newTask, activeSchedule, state.tasks, state.taskTypes);
              if (optimalSlot) {
                newTask = {
                  ...newTask,
                  scheduledTime: optimalSlot.scheduledFor,
                  scheduledFor: optimalSlot.scheduledFor,
                  specificTime: optimalSlot.specificTime
                };
                newTasks = [...state.tasks, newTask];
              }
            }
          }
        }
      }

      return {
        ...state,
        tasks: newTasks,
        slots: newSlots,
        items: newTasks
      };
    }

    case ACTION_TYPES.UPDATE_TASK:
    case ACTION_TYPES.UPDATE_ITEM: {
      const updateData = action.payload || action.item;
      const taskId = updateData.id || updateData.key;

      // Find the old task to detect status/schedule changes
      const oldTask = state.tasks.find(
        t => t.id === taskId || t.key?.toString() === taskId?.toString()
      );

      const updatedTasks = state.tasks.map(task => {
        const isMatch = task.id === taskId || task.key?.toString() === taskId?.toString();
        if (!isMatch) return task;

        return {
          ...task,
          ...updateData,
          updatedAt: timestamp
        };
      });

      // If the task was previously scheduled and is now becoming pending
      // without a scheduled time (e.g. paused→pending, or manually unscheduled),
      // free its old slot so the system can place it (and others) optimally.
      const updatedTask = updatedTasks.find(
        t => t.id === taskId || t.key?.toString() === taskId?.toString()
      );
      let slotsAfterFree = state.slots;
      const wasScheduledBefore = oldTask?.scheduledSlotId;
      const isUnscheduledNow = updatedTask && !updatedTask.scheduledTime;
      if (wasScheduledBefore && isUnscheduledNow) {
        slotsAfterFree = state.slots.map(slot =>
          slot.id === oldTask.scheduledSlotId
            ? { ...slot, assignedTaskId: null, updatedAt: timestamp }
            : slot
        );
      }

      // Try to schedule if task is pending and unscheduled
      const shouldReschedule = updatedTask &&
        state.settings.autoSchedule &&
        updatedTask.status === 'pending' &&
        !updatedTask.scheduledTime &&
        !updatedTask.specificTime;

      if (shouldReschedule) {
        // Use the unified backfill path which handles all unscheduled tasks
        const intermediateState = { ...state, tasks: updatedTasks, slots: slotsAfterFree };
        const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
        if (backfilled) {
          return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
        }
      }

      return {
        ...state,
        tasks: updatedTasks,
        slots: slotsAfterFree,
        items: updatedTasks
      };
    }

    case ACTION_TYPES.DELETE_TASK:
    case ACTION_TYPES.DELETE_ITEM: {
      const deleteData = action.payload || action.item;
      const taskId = deleteData.id || deleteData.key;
      const removedTaskIds = new Set(
        state.tasks
          .filter((task) => task.id === taskId || task.key?.toString() === taskId?.toString())
          .map((task) => getTaskIdentifier(task))
          .filter(Boolean)
      );

      const newTasks = state.tasks.filter(task =>
        task.id !== taskId && task.key?.toString() !== taskId?.toString()
      );
      const newSlots = clearSlotAssignmentsForTaskIds(state.slots, removedTaskIds, timestamp);

      // Backfill: freed slots can now accept unscheduled tasks
      const intermediateState = { ...state, tasks: newTasks, slots: newSlots, settings: state.settings, taskTypes: state.taskTypes };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }

      return {
        ...state,
        tasks: newTasks,
        slots: newSlots,
        items: newTasks
      };
    }

    case ACTION_TYPES.COMPLETE_TASK: {
      const { taskId } = action.payload;
      let nextRecurrence = null;
      const completedTask = state.tasks.find(
        t => t.id === taskId || t.key?.toString() === taskId
      );
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        // Generate next recurrence if applicable
        if (task.recurrence && task.recurrence.frequency !== 'none') {
          nextRecurrence = generateNextRecurrence(task);
        }
        return {
          ...task,
          status: 'completed',
          completedAt: timestamp,
          startedAt: null, // stop any running timer
          updatedAt: timestamp
        };
      });

      if (nextRecurrence) {
        newTasks.push(nextRecurrence);
      }

      // Free the completed task's slot assignment
      const freedSlotId = completedTask?.scheduledSlotId;
      let newSlots = freedSlotId
        ? state.slots.map(slot =>
            slot.id === freedSlotId
              ? { ...slot, assignedTaskId: null, updatedAt: timestamp }
              : slot
          )
        : state.slots;

      // Backfill: try to place unscheduled tasks into available slots
      const intermediateState = { ...state, tasks: newTasks, slots: newSlots };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }

      return { ...state, tasks: newTasks, slots: newSlots, items: newTasks };
    }

    case ACTION_TYPES.PAUSE_TASK: {
      const { taskId } = action.payload;
      const pausedTask = state.tasks.find(
        t => t.id === taskId || t.key?.toString() === taskId
      );
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        return {
          ...task,
          status: 'paused',
          scheduledTime: null,
          scheduledFor: null,
          specificTime: null,
          scheduledSlotId: null,
          updatedAt: timestamp
        };
      });

      // Free the paused task's slot so another task can use it
      const freedSlotId = pausedTask?.scheduledSlotId;
      let newSlots = freedSlotId
        ? state.slots.map(slot =>
            slot.id === freedSlotId
              ? { ...slot, assignedTaskId: null, updatedAt: timestamp }
              : slot
          )
        : state.slots;

      // Backfill freed slot with another unscheduled task
      const intermediateState = { ...state, tasks: newTasks, slots: newSlots };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }

      return { ...state, tasks: newTasks, slots: newSlots, items: newTasks };
    }

    case ACTION_TYPES.RESUME_TASK: {
      const { taskId } = action.payload;
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        return { ...task, status: 'pending', updatedAt: timestamp };
      });

      // Backfill: resumed task is now pending and may need scheduling
      const intermediateState = { ...state, tasks: newTasks };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }

      return { ...state, tasks: newTasks, items: newTasks };
    }

    case ACTION_TYPES.RESCHEDULE_TASK: {
      // Accept both `taskId` (legacy) and `id` (newer call sites). Honor an
      // explicit `scheduledSlotId` when caller passes one (e.g. moveToSlot
      // hands a target slot id; reschedule-by-date passes null to detach).
      const { taskId: payloadTaskId, id: payloadId, scheduledTime, specificTime, specificDay, scheduledSlotId } = action.payload;
      const taskId = payloadTaskId ?? payloadId;
      const oldTask = state.tasks.find((task) => task.id === taskId || task.key?.toString() === taskId);
      const oldSlotId = oldTask?.scheduledSlotId;
      const updatedSlots = oldSlotId
        ? state.slots.map((slot) => {
          if (slot.id !== oldSlotId) return slot;
          return { ...slot, assignedTaskId: null, updatedAt: timestamp };
        })
        : state.slots;

      const nextSlotId = scheduledSlotId !== undefined ? scheduledSlotId : null;
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        return {
          ...task,
          scheduledTime,
          scheduledFor: scheduledTime,
          specificTime,
          specificDay,
          scheduledSlotId: nextSlotId,
          updatedAt: timestamp
        };
      });

      // Backfill: the freed slot may be filled by another unscheduled task
      if (oldSlotId) {
        const intermediateState = { ...state, tasks: newTasks, slots: updatedSlots };
        const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
        if (backfilled) {
          return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
        }
      }

      return { ...state, tasks: newTasks, slots: updatedSlots, items: newTasks };
    }

    case ACTION_TYPES.ADD_SUBTASK: {
      const { taskId, text } = action.payload;
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        const subtasks = [...(task.subtasks || []), {
          id: `sub_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          text,
          completed: false
        }];
        return { ...task, subtasks, updatedAt: timestamp };
      });
      return { ...state, tasks: newTasks, items: newTasks };
    }

    case ACTION_TYPES.TOGGLE_SUBTASK: {
      const { taskId, subtaskId } = action.payload;
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        const subtasks = (task.subtasks || []).map(s =>
          s.id === subtaskId ? { ...s, completed: !s.completed } : s
        );
        return { ...task, subtasks, updatedAt: timestamp };
      });
      return { ...state, tasks: newTasks, items: newTasks };
    }

    case ACTION_TYPES.DELETE_SUBTASK: {
      const { taskId, subtaskId } = action.payload;
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        const subtasks = (task.subtasks || []).filter(s => s.id !== subtaskId);
        return { ...task, subtasks, updatedAt: timestamp };
      });
      return { ...state, tasks: newTasks, items: newTasks };
    }

    case ACTION_TYPES.START_TIMER: {
      const { taskId } = action.payload;
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        return { ...task, startedAt: timestamp, updatedAt: timestamp };
      });
      return { ...state, tasks: newTasks, items: newTasks };
    }

    case ACTION_TYPES.STOP_TIMER: {
      const { taskId } = action.payload;
      const newTasks = state.tasks.map(task => {
        if (task.id !== taskId && task.key?.toString() !== taskId) return task;
        if (!task.startedAt) return task;
        const elapsed = Math.round((new Date(timestamp) - new Date(task.startedAt)) / 60000);
        const actualDuration = (task.actualDuration || 0) + elapsed;
        return { ...task, startedAt: null, actualDuration, updatedAt: timestamp };
      });
      return { ...state, tasks: newTasks, items: newTasks };
    }

    case ACTION_TYPES.BATCH_UPDATE_TASKS: {
      const { updates } = action.payload;
      const updateMap = new Map(updates.map(u => [u.id, u.updates]));

      const newTasks = state.tasks.map(task => {
        const taskUpdates = updateMap.get(task.id) || updateMap.get(task.key?.toString());
        if (!taskUpdates) return task;
        return { ...task, ...taskUpdates, updatedAt: timestamp };
      });

      return { ...state, tasks: newTasks, items: newTasks };
    }

    case ACTION_TYPES.SCHEDULE_TASKS: {
      const tasksToSchedule = action.payload?.tasks || action.tasks || [];
      if (tasksToSchedule.length === 0) {
        return state;
      }

      // Primary path: unified scheduling engine
      const schedulingIds = new Set(
        tasksToSchedule
          .map(task => task?.id?.toString() || task?.key?.toString() || null)
          .filter(Boolean)
      );

      const stateForEngine = {
        slots: state.slots,
        tasks: state.tasks.filter(task => {
          const id = task?.id?.toString() || task?.key?.toString() || null;
          return id ? !schedulingIds.has(id) : true;
        }),
        settings: state.settings,
        taskTypes: state.taskTypes,
      };

      const engineResults = engineScheduleMultiple(tasksToSchedule, stateForEngine);
      const engineScheduledMap = new Map();
      const slotUpdates = new Map();

      engineResults.forEach(({ task, result }) => {
        if (!result) return;
        const taskId = task?.id?.toString() || task?.key?.toString();
        if (!taskId) return;
        engineScheduledMap.set(taskId, {
          scheduledTime: result.scheduledTime,
          scheduledFor: result.scheduledTime,
          specificTime: result.specificTime,
          specificDay: result.specificDay,
          scheduledSlotId: result.slotId || null,
          metadata: {
            ...(task.metadata || {}),
            schedulingReason: result.reason,
            schedulingConfidence: result.confidence,
            scheduledLabel: result.label,
            scheduledDate: result.date,
          },
        });
        if (result.slotId) {
          slotUpdates.set(result.slotId, taskId);
        }
      });

      if (engineScheduledMap.size > 0) {
        const newTasks = state.tasks.map(task => {
          const taskId = task?.id?.toString() || task?.key?.toString();
          const updates = engineScheduledMap.get(taskId);
          if (!updates) return task;
          return { ...task, ...updates, updatedAt: timestamp };
        });

        const newSlots = state.slots.map(slot => {
          const assignedTaskId = slotUpdates.get(slot.id);
          if (!assignedTaskId) return slot;
          return { ...slot, assignedTaskId, updatedAt: timestamp };
        });

        return { ...state, tasks: newTasks, slots: newSlots, items: newTasks };
      }

      // Fallback: slot-based scheduling
      const slotScheduled = scheduleTasksIntoSlots(state, tasksToSchedule, timestamp);
      if (slotScheduled) {
        return {
          ...state,
          tasks: slotScheduled.tasks,
          slots: slotScheduled.slots,
          items: slotScheduled.tasks
        };
      }

      const activeSchedule = state.activeSchedule || getLegacyActiveSchedule();
      if (!activeSchedule) {
        return state;
      }

      const existingTasks = state.tasks.filter(task => {
        const id = task?.id?.toString() || task?.key?.toString() || null;
        return id ? !schedulingIds.has(id) : true;
      });

      const scheduledTasks = batchScheduleTasks(tasksToSchedule, activeSchedule, existingTasks, state.taskTypes);
      const scheduledTaskMap = new Map(
        scheduledTasks.map(task => [task?.id?.toString() || task?.key?.toString(), task])
      );

      const fallbackTasks = state.tasks.map(task => {
        const id = task?.id?.toString() || task?.key?.toString();
        const scheduled = scheduledTaskMap.get(id);
        return scheduled || task;
      });

      return { ...state, tasks: fallbackTasks, items: fallbackTasks };
    }

    case ACTION_TYPES.RESCHEDULE_ALL_TASKS: {
      const pendingTasks = state.tasks
        .filter(item => item.status === 'pending' || item.status === 'paused')
        .map(item => ({
          ...item,
          scheduledTime: null,
          scheduledFor: null,
          specificTime: null,
          specificDay: null,
          scheduledSlotId: null
        }));

      if (pendingTasks.length === 0) return state;

      // Clear old slot assignments for tasks being rescheduled
      const reschedulingIds = new Set(
        pendingTasks.map(t => (t.id || t.key)?.toString()).filter(Boolean)
      );
      const clearedSlots = clearSlotAssignmentsForTaskIds(state.slots, reschedulingIds, timestamp);

      const stateForEngine = {
        slots: clearedSlots,
        tasks: state.tasks.filter(t =>
          !reschedulingIds.has((t.id || t.key)?.toString())
        ),
        settings: state.settings,
        taskTypes: state.taskTypes,
      };

      const engineResults = engineScheduleMultiple(pendingTasks, stateForEngine);
      const scheduledMap = new Map();
      const slotUpdates = new Map();

      engineResults.forEach(({ task, result }) => {
        if (!result) return;
        const taskId = (task.id || task.key)?.toString();
        if (!taskId) return;
        scheduledMap.set(taskId, {
          scheduledTime: result.scheduledTime,
          scheduledFor: result.scheduledTime,
          specificTime: result.specificTime,
          specificDay: result.specificDay,
          scheduledSlotId: result.slotId || null,
          metadata: {
            ...(task.metadata || {}),
            schedulingReason: result.reason,
            scheduledLabel: result.label,
          },
        });
        if (result.slotId) {
          slotUpdates.set(result.slotId, taskId);
        }
      });

      if (scheduledMap.size > 0) {
        const newTasks = state.tasks.map(task => {
          const taskId = (task.id || task.key)?.toString();
          const updates = scheduledMap.get(taskId);
          if (!updates) return task;
          return { ...task, ...updates, updatedAt: timestamp };
        });

        const newSlots = clearedSlots.map(slot => {
          const assignedTaskId = slotUpdates.get(slot.id);
          if (!assignedTaskId) return slot;
          return { ...slot, assignedTaskId, updatedAt: timestamp };
        });

        return { ...state, tasks: newTasks, slots: newSlots, items: newTasks };
      }

      // Fallback paths
      const slotScheduled = scheduleTasksIntoSlots(state, pendingTasks, timestamp);
      if (slotScheduled) {
        return {
          ...state,
          tasks: slotScheduled.tasks,
          slots: slotScheduled.slots,
          items: slotScheduled.tasks
        };
      }

      const activeSchedule = state.activeSchedule || getLegacyActiveSchedule();
      if (activeSchedule) {
        const scheduledTasks = batchScheduleTasks(pendingTasks, activeSchedule, [], state.taskTypes);
        const newTasks = state.tasks.map(item => {
          const scheduledItem = scheduledTasks.find(s => s.key === item.key || s.id === item.id);
          return scheduledItem || item;
        });
        return { ...state, tasks: newTasks, items: newTasks };
      }

      return state;
    }

    // ============================================
    // SLOT ACTIONS
    // ============================================

    case ACTION_TYPES.ADD_SLOT: {
      const slot = createCalendarSlot(action.payload);
      const newSlots = [...state.slots, slot];

      // Backfill: new slot may accept an unscheduled task
      const intermediateState = { ...state, slots: newSlots, settings: state.settings, taskTypes: state.taskTypes };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }

      return { ...state, slots: newSlots };
    }

    case ACTION_TYPES.UPDATE_SLOT: {
      const slotId = action.payload?.slotId || action.payload?.id;
      const updates = action.payload?.updates || action.payload || {};
      const newSlots = state.slots.map(slot =>
        slot.id === slotId ? { ...slot, ...updates, updatedAt: timestamp } : slot
      );
      // Backfill: slot may have been expanded or type changed, so unscheduled tasks may now fit
      const intermediateState = { ...state, slots: newSlots };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }
      return { ...state, slots: newSlots };
    }

    case ACTION_TYPES.DELETE_SLOT: {
      const slotId = action.payload?.slotId || action.payload?.id;
      const deletedSlot = state.slots.find(s => s.id === slotId);
      const remainingSlots = state.slots.filter(s => s.id !== slotId);

      // Unschedule any task assigned to the deleted slot so it can be rescheduled
      let newTasks = state.tasks;
      if (deletedSlot?.assignedTaskId) {
        newTasks = state.tasks.map(task => {
          const taskId = task.id || task.key?.toString();
          if (taskId !== deletedSlot.assignedTaskId) return task;
          return {
            ...task,
            scheduledTime: null,
            scheduledFor: null,
            specificTime: null,
            scheduledSlotId: null,
            updatedAt: timestamp
          };
        });
      }

      // Backfill: displaced task (and any other unscheduled) may fit elsewhere
      const intermediateState = { ...state, tasks: newTasks, slots: remainingSlots };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }
      return { ...state, tasks: newTasks, slots: remainingSlots, items: newTasks };
    }

    case ACTION_TYPES.ASSIGN_TASK_TO_SLOT: {
      const { slotId, taskId } = action.payload;
      const newSlots = state.slots.map(slot =>
        slot.id === slotId ? { ...slot, assignedTaskId: taskId, updatedAt: timestamp } : slot
      );
      return { ...state, slots: newSlots };
    }

    case ACTION_TYPES.CLEAR_SLOT: {
      const { slotId } = action.payload;
      const clearedSlot = state.slots.find(s => s.id === slotId);
      const newSlots = state.slots.map(slot =>
        slot.id === slotId ? { ...slot, assignedTaskId: null, updatedAt: timestamp } : slot
      );

      // Unschedule the displaced task so it can be rescheduled
      let newTasks = state.tasks;
      if (clearedSlot?.assignedTaskId) {
        newTasks = state.tasks.map(task => {
          const tid = task.id || task.key?.toString();
          if (tid !== clearedSlot.assignedTaskId) return task;
          return {
            ...task,
            scheduledTime: null,
            scheduledFor: null,
            specificTime: null,
            scheduledSlotId: null,
            updatedAt: timestamp
          };
        });
      }

      // Backfill: freed slot and displaced task may find better placement
      const intermediateState = { ...state, tasks: newTasks, slots: newSlots };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }
      return { ...state, tasks: newTasks, slots: newSlots, items: newTasks };
    }

    case ACTION_TYPES.BATCH_CREATE_SLOTS: {
      const { slots } = action.payload;
      const newSlots = [...state.slots, ...slots.map(s => createCalendarSlot(s))];

      // Backfill: new slots may accept unscheduled tasks
      const intermediateState = { ...state, slots: newSlots, settings: state.settings, taskTypes: state.taskTypes };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }

      return { ...state, slots: newSlots };
    }

    case ACTION_TYPES.CLEAR_SLOTS_FOR_DATE: {
      const { date, sourceScheduleId } = action.payload;
      const removedSlots = state.slots.filter(slot => {
        if (slot.date !== date) return false;
        if (sourceScheduleId && slot.sourceScheduleId !== sourceScheduleId) return false;
        return true;
      });
      const removedSlotIds = new Set(removedSlots.map(s => s.id));
      const displacedTaskIds = new Set(
        removedSlots.filter(s => s.assignedTaskId).map(s => s.assignedTaskId)
      );
      const newSlots = state.slots.filter(s => !removedSlotIds.has(s.id));

      // Unschedule tasks that were assigned to removed slots
      let newTasks = state.tasks;
      if (displacedTaskIds.size > 0) {
        newTasks = state.tasks.map(task => {
          const tid = task.id || task.key?.toString();
          if (!displacedTaskIds.has(tid)) return task;
          return {
            ...task,
            scheduledTime: null,
            scheduledFor: null,
            specificTime: null,
            scheduledSlotId: null,
            updatedAt: timestamp
          };
        });
      }

      // Backfill: displaced tasks may fit in remaining slots
      const intermediateState = { ...state, tasks: newTasks, slots: newSlots };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return { ...state, tasks: backfilled.tasks, slots: backfilled.slots, items: backfilled.tasks };
      }
      return { ...state, tasks: newTasks, slots: newSlots, items: newTasks };
    }

    // ============================================
    // TAG ACTIONS
    // ============================================

    case ACTION_TYPES.ADD_TAG: {
      const tag = createTag(action.payload);
      return { ...state, tags: [...state.tags, tag] };
    }

    case ACTION_TYPES.UPDATE_TAG: {
      const { tagId, updates } = action.payload;
      const newTags = state.tags.map(tag =>
        tag.id === tagId ? { ...tag, ...updates, updatedAt: timestamp } : tag
      );
      return { ...state, tags: newTags };
    }

    case ACTION_TYPES.DELETE_TAG: {
      const { tagId } = action.payload;
      // Don't delete system tags
      const tag = state.tags.find(t => t.id === tagId);
      if (tag?.isSystem) return state;
      return { ...state, tags: state.tags.filter(t => t.id !== tagId) };
    }

    // ============================================
    // TASK TYPE ACTIONS
    // ============================================

    case ACTION_TYPES.ADD_TASK_TYPE: {
      const taskType = createTaskType(action.payload || action.taskType);
      return { ...state, taskTypes: [...state.taskTypes, taskType] };
    }

    case ACTION_TYPES.UPDATE_TASK_TYPE: {
      const updateData = action.payload || action.taskType;
      const typeId = updateData.id;
      const newTypes = state.taskTypes.map(type =>
        type.id === typeId ? { ...type, ...updateData, updatedAt: timestamp } : type
      );
      return { ...state, taskTypes: newTypes };
    }

    case ACTION_TYPES.DELETE_TASK_TYPE: {
      const typeId = action.payload?.typeId || action.taskTypeId;
      if (typeId === 'default') return state;
      return { ...state, taskTypes: state.taskTypes.filter(t => t.id !== typeId) };
    }

    // ============================================
    // SCHEDULE ACTIONS
    // ============================================

    case ACTION_TYPES.ADD_SCHEDULE: {
      const schedule = createSchedule(action.payload);
      return { ...state, schedules: [...state.schedules, schedule] };
    }

    case ACTION_TYPES.UPDATE_SCHEDULE: {
      const { scheduleId, updates } = action.payload;
      const newSchedules = state.schedules.map(schedule =>
        schedule.id === scheduleId ? { ...schedule, ...updates, updatedAt: timestamp } : schedule
      );
      return { ...state, schedules: newSchedules };
    }

    case ACTION_TYPES.DELETE_SCHEDULE: {
      const { scheduleId } = action.payload;
      const newSchedules = state.schedules.filter(s => s.id !== scheduleId);
      const newActiveSchedule = state.activeScheduleId === scheduleId ? null : state.activeSchedule;
      const newActiveScheduleId = state.activeScheduleId === scheduleId ? null : state.activeScheduleId;
      return {
        ...state,
        schedules: newSchedules,
        activeSchedule: newActiveSchedule,
        activeScheduleId: newActiveScheduleId
      };
    }

    case ACTION_TYPES.SET_ACTIVE_SCHEDULE: {
      const { schedule, scheduleId } = action.payload;
      return {
        ...state,
        activeSchedule: schedule,
        activeScheduleId: scheduleId || schedule?.id
      };
    }

    case ACTION_TYPES.APPLY_SCHEDULE:
    case ACTION_TYPES.APPLY_TEMPLATE_BLOCKS: {
      const { blocks = [], options = {} } = action.payload || {};
      const { overrideExisting = false, mergeWithExisting = true } = options;

      if (!Array.isArray(blocks) || blocks.length === 0) {
        return state;
      }

      const generatedSlots = blocks.map((block, index) =>
        createCalendarSlot(normalizeTemplateBlockToSlot(block, index))
      );

      const affectedDates = new Set(generatedSlots.map(slot => slot.date));
      let baseSlots = state.slots;

      if (overrideExisting || !mergeWithExisting) {
        baseSlots = baseSlots.filter(slot => !affectedDates.has(slot.date));
      }

      // Prevent duplicate slots when re-applying the same schedule/range.
      const slotSignature = (slot) => [
        slot.date,
        slot.startTime,
        slot.endTime,
        slot.label || '',
        slot.sourceScheduleId || '',
        slot.sourceBlockId || ''
      ].join('|');

      const existingSignatures = new Set(baseSlots.map(slotSignature));
      const uniqueGeneratedSlots = generatedSlots.filter(slot => {
        const signature = slotSignature(slot);
        if (existingSignatures.has(signature)) {
          return false;
        }
        existingSignatures.add(signature);
        return true;
      });

      // Legacy template-created tasks should be removed: templates now define
      // scheduling windows/slots, while user tasks fill those windows.
      const nonTemplateTasks = state.tasks.filter(task => !task.isTemplateBlock);
      const newSlots = [...baseSlots, ...uniqueGeneratedSlots];

      // Backfill: auto-place unscheduled tasks into the newly created slots
      const intermediateState = { ...state, tasks: nonTemplateTasks, slots: newSlots, settings: state.settings, taskTypes: state.taskTypes };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return {
          ...state,
          slots: backfilled.slots,
          tasks: backfilled.tasks,
          items: backfilled.tasks
        };
      }

      return {
        ...state,
        slots: newSlots,
        tasks: nonTemplateTasks,
        items: nonTemplateTasks
      };
    }

    case ACTION_TYPES.AUTO_SLOT_TASKS_INTO_TEMPLATE: {
      const { templateBlocks } = action.payload;

      const unscheduledTasks = state.tasks.filter(
        item => item.status === 'pending' && !item.scheduledTime && !item.isTemplateBlock
      );

      if (unscheduledTasks.length === 0 || !templateBlocks?.length) {
        return state;
      }

      const newTasks = state.tasks.map(item => {
        if (item.scheduledTime || item.isTemplateBlock || item.status !== 'pending') {
          return item;
        }

        // Find matching blocks
        const eligibleBlocks = templateBlocks.filter(block =>
          block.type === item.taskType ||
          block.type === item.primaryType ||
          block.type === 'buffer'
        );

        if (eligibleBlocks.length === 0) return item;

        const sortedBlocks = [...eligibleBlocks].sort((a, b) =>
          new Date(a.startTime) - new Date(b.startTime)
        );

        for (const block of sortedBlocks) {
          const blockStart = new Date(block.startTime);
          const blockEnd = new Date(block.endTime);
          const taskDuration = item.duration || 30;
          const blockDuration = (blockEnd - blockStart) / 60000;

          if (blockDuration >= taskDuration && blockStart > new Date()) {
            const hasConflict = state.tasks.some(otherItem => {
              if (!otherItem.scheduledTime || otherItem.id === item.id) return false;
              const otherStart = new Date(otherItem.scheduledTime);
              const otherEnd = new Date(otherStart.getTime() + (otherItem.duration || 30) * 60000);
              const taskEnd = new Date(blockStart.getTime() + taskDuration * 60000);
              return blockStart < otherEnd && taskEnd > otherStart;
            });

            if (!hasConflict) {
              return {
                ...item,
                scheduledTime: blockStart.toISOString(),
                specificTime: blockStart.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }),
                scheduledFor: blockStart.toISOString().split('T')[0],
                specificDay: format(blockStart, 'EEEE'),
                assignedToTemplate: true
              };
            }
          }
        }

        return item;
      });

      return { ...state, tasks: newTasks, items: newTasks };
    }

    // ============================================
    // PALACE ACTIONS
    // ============================================

    case 'ADD_PALACE': {
      const newPalaces = [...(state.palaces || []), action.payload];
      return { ...state, palaces: newPalaces };
    }

    case 'UPDATE_PALACE': {
      const { palaceId, updates } = action.payload;
      const newPalaces = (state.palaces || []).map(palace =>
        palace.id === palaceId ? { ...palace, ...updates, updatedAt: timestamp } : palace
      );
      return { ...state, palaces: newPalaces };
    }

    case 'DELETE_PALACE': {
      const { palaceId } = action.payload;
      const newPalaces = (state.palaces || []).filter(p => p.id !== palaceId);
      return { ...state, palaces: newPalaces };
    }

    case 'ADD_PALACE_LOCATION': {
      const { palaceId, location } = action.payload;
      const newPalaces = (state.palaces || []).map(palace => {
        if (palace.id !== palaceId) return palace;
        return {
          ...palace,
          locations: [...palace.locations, location],
          updatedAt: timestamp
        };
      });
      return { ...state, palaces: newPalaces };
    }

    case 'UPDATE_PALACE_LOCATION': {
      const { palaceId, locationId, updates } = action.payload;
      const newPalaces = (state.palaces || []).map(palace => {
        if (palace.id !== palaceId) return palace;
        return {
          ...palace,
          locations: palace.locations.map(loc =>
            loc.id === locationId ? { ...loc, ...updates, updatedAt: timestamp } : loc
          ),
          updatedAt: timestamp
        };
      });
      return { ...state, palaces: newPalaces };
    }

    case 'DELETE_PALACE_LOCATION': {
      const { palaceId, locationId } = action.payload;
      const newPalaces = (state.palaces || []).map(palace => {
        if (palace.id !== palaceId) return palace;
        return {
          ...palace,
          locations: palace.locations.filter(loc => loc.id !== locationId),
          updatedAt: timestamp
        };
      });
      return { ...state, palaces: newPalaces };
    }

    // ============================================
    // SETTINGS ACTIONS
    // ============================================

    case ACTION_TYPES.UPDATE_SETTINGS: {
      const newSettings = { ...state.settings, ...action.payload };
      const settingsChanged = { ...state, settings: newSettings };

      // If defaultDaySlots changed, backfill unscheduled tasks into the new framework
      if (action.payload.defaultDaySlots) {
        const backfilled = backfillUnscheduledTasks(settingsChanged, timestamp);
        if (backfilled) {
          return {
            ...state,
            settings: newSettings,
            tasks: backfilled.tasks,
            slots: backfilled.slots,
            items: backfilled.tasks
          };
        }
      }

      return {
        ...state,
        settings: newSettings
      };
    }

    // ============================================
    // DATE ACTIONS
    // ============================================

    case ACTION_TYPES.UPDATE_DATE: {
      return { ...state, date: getInitialDate() };
    }

    case ACTION_TYPES.DAILY_RESET:
    case ACTION_TYPES.RESET_ALL: {
      // Move completed tasks out, reset paused to pending, clear old schedules
      const newTasks = state.tasks
        .filter(item => item.status !== 'completed')
        .map(item => ({
          ...item,
          status: item.status === 'paused' ? 'pending' : item.status,
          // Clear yesterday's scheduling so tasks get fresh placement
          scheduledTime: null,
          scheduledFor: null,
          specificTime: null,
          scheduledSlotId: null,
          updatedAt: timestamp
        }));

      // Clear old slot assignments (keep slot structure, just unassign tasks)
      const resetSlots = state.slots.map(slot => ({
        ...slot,
        assignedTaskId: null,
        updatedAt: timestamp
      }));

      // Generate today's slots from defaultDaySlots if available
      const defaultDaySlots = state.settings?.defaultDaySlots || [];
      let newSlots = resetSlots;
      if (defaultDaySlots.length > 0) {
        const today = new Date();
        const todayStr = format(today, 'yyyy-MM-dd');
        const dayName = format(today, 'EEEE');
        const todaySlotsExist = resetSlots.some(s => s.date === todayStr);

        if (!todaySlotsExist) {
          const todayDefaults = defaultDaySlots.filter(s => s.day === dayName);
          const generatedSlots = todayDefaults.map(slot =>
            createCalendarSlot({
              date: todayStr,
              startTime: slot.startTime,
              endTime: slot.endTime,
              slotType: slot.slotType,
              label: slot.label,
              color: slot.color,
              flexibility: slot.flexibility || 'preferred',
              allowedTags: Array.isArray(slot.allowedTags) ? slot.allowedTags : [],
              sourceScheduleId: 'default-day-slots',
              sourceBlockId: slot.id,
            })
          );
          newSlots = [...resetSlots, ...generatedSlots];
        }
      }

      // Backfill: place all pending tasks into available slots
      const intermediateState = { ...state, tasks: newTasks, slots: newSlots, settings: state.settings, taskTypes: state.taskTypes };
      const backfilled = backfillUnscheduledTasks(intermediateState, timestamp);
      if (backfilled) {
        return {
          ...state,
          tasks: backfilled.tasks,
          slots: backfilled.slots,
          items: backfilled.tasks,
          date: getInitialDate()
        };
      }

      return {
        ...state,
        tasks: newTasks,
        slots: newSlots,
        items: newTasks,
        date: getInitialDate()
      };
    }

    // ============================================
    // AUDIT ACTIONS
    // ============================================

    case ACTION_TYPES.ADD_AUDIT_ENTRY: {
      const entry = createAuditEntry(action.payload);
      const newAuditLog = [...state.auditLog, entry].slice(-1000); // Keep last 1000
      return { ...state, auditLog: newAuditLog };
    }

    default:
      console.warn(`[AppContext] Unknown action type: ${action.type}`);
      return state;
  }
}

// ============================================
// CONTEXT PROVIDER
// ============================================

export function AppStateProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const dbRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Initialize from database
  useEffect(() => {
    async function initializeState() {
      try {
        dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

        const db = await getAdapter();
        dbRef.current = db;

        const savedState = await db.getState();

        const tasks = savedState.tasks || savedState.items || [];

        // Seed policy: an existing user keeps whatever they saved.
        // A fresh install (no taskTypes, no wheels) gets the shipped
        // defaults so the app is usable from the first click.
        const isFreshInstall =
          (!Array.isArray(savedState.taskTypes) || savedState.taskTypes.length === 0) &&
          (!savedState.settings?.wheels || savedState.settings.wheels.length === 0);

        const taskTypes = Array.isArray(savedState.taskTypes) && savedState.taskTypes.length > 0
          ? savedState.taskTypes
          : (isFreshInstall ? SEED_TASK_TYPES : DEFAULT_TASK_TYPES);

        const mergedSettings = { ...initialState.settings, ...(savedState.settings || {}) };
        if (isFreshInstall) {
          mergedSettings.wheels = SEED_WHEELS;
          mergedSettings.defaultWheelId = SEED_DAY_WHEEL_ID;
        }

        const payload = {
          tasks,
          slots: savedState.slots || [],
          tags: savedState.tags?.length > 0 ? savedState.tags : DEFAULT_TAGS,
          taskTypes,
          schedules: savedState.schedules || [],
          palaces: savedState.palaces || [],
          activeScheduleId: savedState.activeScheduleId,
          activeSchedule: await db.getActiveSchedule(),
          settings: mergedSettings,
          date: getInitialDate()
        };

        dispatch({ type: ACTION_TYPES.INIT_STATE, payload });
        telemetryLog('app:boot', { freshInstall: isFreshInstall, ...snapshotState({ ...payload, isInitialized: true }) });
      } catch (error) {
        console.error('[AppContext] Initialization error:', error);
        telemetryLog('app:boot:error', { message: error?.message });
        dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error.message });
      }
    }

    initializeState();
  }, []);

  // Debounced save to database
  useEffect(() => {
    if (!state.isInitialized || !dbRef.current) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce saves
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await dbRef.current.saveState({
          tasks: state.tasks,
          slots: state.slots,
          tags: state.tags,
          taskTypes: state.taskTypes,
          schedules: state.schedules,
          palaces: state.palaces,
          activeScheduleId: state.activeScheduleId,
          settings: state.settings,
          items: state.tasks // Legacy compatibility
        });

        // Also save to legacy 'state' key for backwards compatibility
        const legacyState = {
          items: state.tasks,
          taskTypes: state.taskTypes,
          date: state.date,
          activeSchedule: state.activeSchedule
        };
        localStorage.setItem('state', JSON.stringify(legacyState));
      } catch (error) {
        console.error('[AppContext] Save error:', error);
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state.tasks, state.slots, state.tags, state.taskTypes, state.schedules, state.palaces, state.activeScheduleId, state.settings, state.isInitialized]);

  // Wrapped dispatch with audit logging
  const auditDispatch = useCallback((action) => {
    // Actions that should be audited
    const auditableActions = [
      ACTION_TYPES.ADD_TASK, ACTION_TYPES.UPDATE_TASK, ACTION_TYPES.DELETE_TASK,
      ACTION_TYPES.COMPLETE_TASK, ACTION_TYPES.PAUSE_TASK, ACTION_TYPES.RESUME_TASK,
      ACTION_TYPES.ADD_SLOT, ACTION_TYPES.UPDATE_SLOT, ACTION_TYPES.DELETE_SLOT,
      ACTION_TYPES.ADD_TAG, ACTION_TYPES.UPDATE_TAG, ACTION_TYPES.DELETE_TAG,
      ACTION_TYPES.ADD_TASK_TYPE, ACTION_TYPES.UPDATE_TASK_TYPE, ACTION_TYPES.DELETE_TASK_TYPE,
      ACTION_TYPES.ADD_SCHEDULE, ACTION_TYPES.UPDATE_SCHEDULE, ACTION_TYPES.DELETE_SCHEDULE,
      ACTION_TYPES.SET_ACTIVE_SCHEDULE, ACTION_TYPES.APPLY_SCHEDULE,
      // Legacy
      ACTION_TYPES.ADD_ITEM, ACTION_TYPES.UPDATE_ITEM, ACTION_TYPES.DELETE_ITEM
    ];

    const payload = action.payload || action.item || {};
    const entityType = getEntityTypeFromAction(action.type);
    const auditAction = getAuditActionFromAction(action.type);
    const entitySnapshot = getEntitySnapshotForAction(state, entityType, payload);
    const deleteAction = isDeleteAction(action.type);

    // Dispatch the action
    dispatch(action);

    // Create audit entry if action is auditable
    if (auditableActions.includes(action.type)) {
      const auditPayload = {
        action: auditAction,
        entityType,
        entityId: entitySnapshot.entityId,
        entityName: entitySnapshot.entityName,
        previousState: deleteAction ? entitySnapshot.previousState : null,
        newState: deleteAction ? null : payload,
        source: CHANGE_SOURCES.USER
      };

      dispatch({
        type: ACTION_TYPES.ADD_AUDIT_ENTRY,
        payload: auditPayload
      });

      // Also persist to database
      if (dbRef.current) {
        dbRef.current.createAuditEntry(auditPayload)
          .catch(err => console.error('[AppContext] Audit log error:', err));
      }
    }
  }, [state]);

  return (
    <div className="App">
      <AppContext.Provider value={[state, auditDispatch]}>
        {children}
      </AppContext.Provider>
    </div>
  );
}

// ============================================
// HOOKS
// ============================================

export function useAppState() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context[0];
}

export function useAppReducer() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppReducer must be used within AppStateProvider');
  }
  return context[1];
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppStateProvider');
  }
  return context;
}

// Legacy hook for backwards compatibility
export function useItems() {
  const { tasks } = useAppState();

  const pending = tasks.filter(item => item.status === 'pending');
  const paused = tasks.filter(item => item.status === 'paused');
  const completed = tasks.filter(item => item.status === 'completed');

  return { pending, paused, completed };
}

// New specialized hooks
export function useTasks(filters = {}) {
  const { tasks } = useAppState();

  return tasks.filter(task => {
    if (filters.status && task.status !== filters.status) return false;
    if (filters.primaryType && task.primaryType !== filters.primaryType && task.taskType !== filters.primaryType) return false;
    if (filters.tags && !filters.tags.some(tag => task.tags?.includes(tag))) return false;
    return true;
  });
}

export function useSlots(date = null) {
  const { slots } = useAppState();

  if (date) {
    return slots.filter(slot => slot.date === date);
  }
  return slots;
}

export function useTags() {
  const { tags } = useAppState();
  return tags;
}

export function useTaskTypes() {
  const { taskTypes } = useAppState();
  return taskTypes;
}

export function useSchedules() {
  const { schedules, activeSchedule, activeScheduleId } = useAppState();
  return { schedules, activeSchedule, activeScheduleId };
}

export function useSettings() {
  const { settings } = useAppState();
  const dispatch = useAppReducer();

  const updateSettings = useCallback((updates) => {
    dispatch({ type: ACTION_TYPES.UPDATE_SETTINGS, payload: updates });
  }, [dispatch]);

  return [settings, updateSettings];
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function getEntityTypeFromAction(actionType) {
  if (actionType.includes('TASK') || actionType.includes('ITEM')) return ENTITY_TYPES.TASK;
  if (actionType.includes('SLOT')) return ENTITY_TYPES.SLOT;
  if (actionType.includes('TAG')) return ENTITY_TYPES.TAG;
  if (actionType.includes('TYPE')) return ENTITY_TYPES.TASK_TYPE;
  if (actionType.includes('SCHEDULE')) return ENTITY_TYPES.SCHEDULE;
  return ENTITY_TYPES.TASK;
}

function getAuditActionFromAction(actionType) {
  if (actionType.includes('ADD') || actionType.includes('CREATE')) return AUDIT_ACTIONS.CREATE;
  if (actionType.includes('UPDATE')) return AUDIT_ACTIONS.UPDATE;
  if (actionType.includes('DELETE')) return AUDIT_ACTIONS.DELETE;
  if (actionType.includes('COMPLETE')) return AUDIT_ACTIONS.TASK_COMPLETE;
  if (actionType.includes('PAUSE')) return AUDIT_ACTIONS.TASK_PAUSE;
  if (actionType.includes('RESUME')) return AUDIT_ACTIONS.TASK_RESUME;
  if (actionType.includes('APPLY')) return AUDIT_ACTIONS.SCHEDULE_APPLY;
  return AUDIT_ACTIONS.UPDATE;
}

function getEntitySnapshotForAction(state, entityType, payload) {
  const entityId = getEntityIdFromPayload(payload);
  const collection = getCollectionForEntityType(state, entityType);
  const previousState = entityId
    ? collection.find(entity => matchesEntityId(entity, entityId)) || null
    : null;

  return {
    entityId: entityId || previousState?.id || previousState?.key || null,
    entityName:
      payload.text ||
      payload.name ||
      payload.label ||
      payload.schedule?.name ||
      previousState?.text ||
      previousState?.name ||
      previousState?.label ||
      null,
    previousState
  };
}

function getCollectionForEntityType(state, entityType) {
  switch (entityType) {
    case ENTITY_TYPES.TASK:
      return state.tasks || [];
    case ENTITY_TYPES.SLOT:
      return state.slots || [];
    case ENTITY_TYPES.TAG:
      return state.tags || [];
    case ENTITY_TYPES.TASK_TYPE:
      return state.taskTypes || [];
    case ENTITY_TYPES.SCHEDULE:
      return state.schedules || [];
    default:
      return [];
  }
}

function getEntityIdFromPayload(payload = {}) {
  return (
    payload.id ||
    payload.key ||
    payload.taskId ||
    payload.slotId ||
    payload.tagId ||
    payload.scheduleId ||
    payload.schedule?.id ||
    null
  );
}

function matchesEntityId(entity, targetId) {
  if (!entity || !targetId) return false;
  return (
    entity.id?.toString() === targetId.toString() ||
    entity.key?.toString() === targetId.toString()
  );
}

function isDeleteAction(actionType) {
  return actionType.includes('DELETE');
}

export default AppContext;
